import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { useScheduledJobActions } from '../hooks/use-scheduled-job-actions';
import type { ScheduledJobItem } from '../services/scheduled-jobs.types';
import { EditScheduledJobModal } from './edit-scheduled-job-modal';
import { ScheduledJobRow } from './scheduled-job-row';

type ScheduledJobListProps = {
  jobs: ScheduledJobItem[];
  isLoading: boolean;
  isError: boolean;
  emptyMessage: string;
  /** Simpler rows for the agent settings drawer, which already draws a border around the list. */
  compact?: boolean;
};

/** Scheduled job rows with their edit modal and delete, used by the scheduler page and agent settings. */
export function ScheduledJobList({
  jobs,
  isLoading,
  isError,
  emptyMessage,
  compact = false,
}: ScheduledJobListProps) {
  const { t } = useTranslation();
  const { deleteJob, openChat, isDeleting } = useScheduledJobActions();
  const [editingJob, setEditingJob] = useState<ScheduledJobItem | null>(null);

  const handleDeleteJob = async (job: ScheduledJobItem) => {
    await deleteJob(job);
    setEditingJob((current) => (current?.id === job.id ? null : current));
  };

  const handleOpenChat = (job: ScheduledJobItem) => {
    setEditingJob(null);
    openChat(job);
  };

  const stateClassName = compact ? 'py-6' : 'rounded-lg border border-dashed p-6';

  const renderJobs = () => {
    if (isLoading) {
      return (
        <div
          className={cn(
            'flex items-center justify-center gap-2 text-sm text-muted-foreground',
            stateClassName
          )}
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('LOADING_SCHEDULED_JOBS', { defaultValue: 'Loading scheduled jobs…' })}
        </div>
      );
    }

    if (isError) {
      return (
        <div className={cn('text-center text-sm text-destructive', stateClassName)}>
          {t('FAILED_TO_LOAD_SCHEDULED_JOBS', {
            defaultValue: 'Failed to load scheduled jobs.',
          })}
        </div>
      );
    }

    if (jobs.length === 0) {
      return (
        <div className={cn('text-center text-sm text-muted-foreground', stateClassName)}>
          {emptyMessage}
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-4">
        {jobs.map((job) => (
          <ScheduledJobRow
            key={job.id}
            job={job}
            onEdit={setEditingJob}
            // The row menu deletes right away; the toast reports a failure.
            onDelete={(target) => void handleDeleteJob(target).catch(() => undefined)}
            isDeleting={isDeleting}
            compact={compact}
          />
        ))}
      </div>
    );
  };

  return (
    <>
      {renderJobs()}
      <EditScheduledJobModal
        open={!!editingJob}
        job={editingJob}
        onOpenChange={(open) => {
          if (!open) setEditingJob(null);
        }}
        onJobSaved={(saved) => {
          setEditingJob((current) => (current?.id === saved.id ? saved : current));
        }}
        onOpenChat={handleOpenChat}
        onDelete={handleDeleteJob}
      />
    </>
  );
}
